/* eslint-disable no-console */
import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Container, Divider, Grid, Paper, Typography } from '@mui/material';
import MultiSelectComponent from '../../common/MultiSelectComponent';
import TextFieldComponent from '../../common/TextFieldComponent';
import BookingAttributionReadOnly from '../../../components/booking/BookingAttributionReadOnly';
import BookingDatePicker from '../../common/BookingDatePicker';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { useDispatch } from 'react-redux';
import {
    setAddress,
    setCity,
    setCountry,
    setEmail,
    setFirstName,
    setLastName,
    setNumberOfGuests,
    setPersoCity,
    setPhone,
    setSelectedApartment,
    setSomeoneElseEmail,
    setSomeoneElseFirstName,
    setSomeoneElseLastName,
    setSomeoneElsePhone,
    setZipCode,
} from '../../../store/slices/bookingCreationSlice';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import PhoneNumberComponent from '../../common/PhoneNumberComponent';
import { useCountryOptions } from '../../../hooks/useCountryOptions';
import { useTranslation } from 'react-i18next';
import { useUpdateSetupTimeMutation } from '../../../api/booking/bookingApiSlice';
import { useGetOrderUsageLogQuery } from '../../../api/booking/orderUsageLogApiSlice';
import ReturnPhotoThumbnail from './ReturnPhotoThumbnail';
import PaymentHistorySection from './PaymentHistorySection';
import { SomeoneElseDataValidation } from '../../../types/bookingCreation';
import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { makeAssetDetailsUrl } from '../../../routes';
import StatusBadge from '../../../components/StatusBadge';
import CustomButton from '../../../components/CustomButton';
import ReservationSummary from '../../../components/ReservationSummary';
import { useSavedBookingPrice } from '../../../hooks/useSavedBookingPrice';
import { hasPriceAdjustment } from '../../../utils/priceAdjustment';
import { invalidateSavedBookingPrice } from '../../../api/booking/pricePreviewApiSlice';
import { buildOrderAddonData, extractAddress, formatDateForDisplay, formatDateTime, sortByLocaleName } from '../../../utils';
import { stationPickerDay, zoneOfLocation } from '../../../utils/stationTime';
import { instantToLocalInputValue, localInputValueToInstant } from '../../../utils/dateTimeUtils';
import ContractConcludedInfo from './ContractConcludedInfo';
import BookingApprovalInfo, { BookingApprovalLabel } from './BookingApprovalInfo';
import DeclineBookingDialog from '../../../components/booking/DeclineBookingDialog';
import { useDeclineBooking } from '../../../hooks/useDeclineBooking';
import { toApprovalState } from '../../../utils/bookingApproval';
import isCoveredByOrganization, {
    coveredByOf, coveringOrganizationNameOf, detailPaymentBadgeStatus, isFreeByPriceBooking,
} from '../../../utils/bookingCoverage';
import BookingDetailsAdditionalServices
    from '../CreateBooking/components/AdditionalServices/BookingDetailsAdditionalServices';
import CustomSeparator from '../../../components/CustomBreadCrumb';
import { makeRoomBookingEditsUrl, paths } from '../../../routes';
import { setConfirmationDialogContent, setIsConfirmationDialog } from '../../../store/slices/globalSlice';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import CancellationFeeDialog from '../../../components/booking/CancellationFeeDialog';
import CancellationFeeInfo from '../../../components/booking/CancellationFeeInfo';
import {
    cancelErrorMessageKey,
    invoiceCorrectionMessage,
    useCancellationFeeStep,
} from '../../../hooks/useCancellationFeeStep';
import { useCancelBooking } from '../../../flows/cancelBookingFlow';
import { cancelOutcomeMessage } from '../../../flows/cancelOutcome';
import { useCustomerMailHint } from '../../../hooks/useCustomerMailHint';
import { useCustomerMailState } from '../../../hooks/useCustomerMailState';
import { CustomerMailBlockedReason } from '../../../components/booking/CustomerMailBlockedNote';
import InfoTooltip from '../../../components/InfoTooltip';
import { CancelSaveMode, CancellationFeeChoice } from '../../../api/booking/bookingWriteApiSlice';
import SnackbarComponent from '../../common/SnackbarComponent';
import IssueInvoiceSection from '../../../components/invoice/IssueInvoiceSection';
import { useIsInvoiceIssuingTenant } from '../../../hooks/useIsInvoiceIssuingTenant';
import Logger from '../../../common/logger';
import i18n from 'i18next';
import { getCurrentLang } from '../../../i18n/i18n';
import { makeHasPermissionSelector } from '../../../selector/authenticationSelector';
import { useIsMaster } from '../../../common/useIsMaster';
import Permission from '../../../common/permissions';
import { useInvoiceConfig } from '../../../hooks/useInvoiceConfig';
import { useDeleteBooking } from '../../../flows/deleteBookingFlow';
import { deleteBookingErrorMessage } from '../../../utils/deleteBookingErrorMessage';
import PageHeader from '../../../components/PageHeader';
import PageHeadline from '../../../components/PageHeadline';
import { generateInvoicePdf } from '../../../utils/invoicePdf';
import { InvoiceValidationError, validateInvoiceData } from '../../../utils/invoiceValidation';
import { commentLabelKey } from '../../../utils/bookingCommentLabel';
import { useBookingDetailsForOrder } from '../../../hooks/useBookingDetailsForOrder';
import InvoiceOptionsDialog, { InvoiceOptions } from '../../../components/InvoiceOptionsDialog';
import ManualInvoiceNumber from '../../../components/invoice/ManualInvoiceNumber';

dayjs.extend(isSameOrBefore);

const BookingDetailsForRoomRental = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const {
        options: countryOptions,
        toLabel: toCountryLabel,
        toValue: toCountryValue,
    } = useCountryOptions();
    const [apartmentNames, setApartmentNames] = useState<string[]>([]);
    const [dialogAction, setDialogAction] = useState<'cancel' | 'delete' | null>(null);
    const [isChangingDate, setIsChangingDate] = useState(false);
    const [invoiceErrors, setInvoiceErrors] = useState<InvoiceValidationError[]>([]);
    const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);

    const [bookingFor, setBookingFor] = useState<'customer' | 'someone_else'>('customer');
    const params = useParams<{ bookingId: string }>();
    const orderId = params.bookingId ?? '';
    const [snackbarData, setSnackbarData] = useState({
        open: false,
        message: '',
        severity: 'info' as 'success' | 'info' | 'warning' | 'error',
    });
    const { data, refetch, bookingDetails, showBookingDetails } = useBookingDetailsForOrder(orderId);
    // #1203: the price comes from the one backend computation, asked with the
    // booking id — not re-derived from the legacy payment fields.
    const { breakdown, previewStatus } = useSavedBookingPrice(orderId);
    // Generic check-in/checkout timestamps (usage_log). Only populated when the
    // customer used the check-in/checkout flow; null otherwise — the fields below
    // render only when a value exists.
    const { data: usageLog } = useGetOrderUsageLogQuery({ orderId }, { skip: !orderId });

    const [updateSetupTime] = useUpdateSetupTimeMutation();

    const cancelBooking = useCancelBooking();
    // W1-6 (#3042): a booking whose rule charges a fee is cancelled through the fee dialog.
    const feeStep = useCancellationFeeStep(orderId);
    // M8 approval (#1809): "Ablehnen" on a reservation still awaiting approval.
    const decline = useDeclineBooking(
        (message, severity) => setSnackbarData({ open: true, message, severity }),
        () => setTimeout(() => navigate(paths.rentalBooking), 1500),
    );
    const isAwaitingApproval = toApprovalState(bookingDetails?.approval?.state) === 'awaiting';
    const fetchMailHint = useCustomerMailHint();
    // #1993: three states. Falls back to `isMailSent` while the event
    // read is in flight or unavailable, so the badge never renders worse
    // than it did before this existed.
    const mailBadge = useCustomerMailState(
        orderId, bookingDetails?.isMailSent, bookingDetails?.locationTimeZone,
    );
    // Bumped every time the confirm dialog is opened, so a late-arriving
    // mail hint can tell whether it is still the dialog on screen.
    const dialogRequestRef = React.useRef(0);
    const deleteBooking = useDeleteBooking();

    const breadcrumbItems = [
        { label: t('booking_details.booking'), href: paths.rentalBooking },
        { label: data?.bookingNumber ?? '', isActive: true },
    ];

    const onFirstNameChange = (firstName: string) => dispatch(setFirstName({ firstName }));
    const onLastNameChange = (lastName: string) => dispatch(setLastName({ lastName }));
    const onPhoneChange = (phone: string) => {
        dispatch(setPhone({ phone: phone }));
    };
    const onEmailChange = (email: string) => dispatch(setEmail({ email }));

    const onAddressChange = (address: string) => dispatch(setAddress({ address }));
    const onCityChange = (city: string) => dispatch(setPersoCity({ city }));
    const onCountryChange = (country: string) => {
        dispatch(setCountry({ country: country }));
    };
    const onZipCodeChange = (zipCode: string) => {
        dispatch(setZipCode({ zipCode }));
    };

    const onSomeoneElseFirstNameChange = (firstName: string) => {
        dispatch(setSomeoneElseFirstName({ firstName: firstName }));
    };
    const onSomeoneElseLastNameChange = (lastName: string) => {
        dispatch(setSomeoneElseLastName({ lastName: lastName }));
    };
    const onSomeoneElseEmailChange = (email: string) => {
        dispatch(setSomeoneElseEmail({ email: email }));
    };
    const onSomeoneElsePhoneChange = (phone: string) => {
        dispatch(setSomeoneElsePhone({ phone: phone }));
    };

    const personalDetails = useSelector((state: RootState) => state.bookingCreation.bookingDetails?.customerDetails);
    const loadingCancelBooking = useSelector((state: RootState) => state.global.isLoading);
    const someoneElseDetails = useSelector((state: RootState) => state.bookingCreation.bookingDetails?.guestDetails);
    const city = useSelector((state: RootState) => state.bookingCreation.bookingDetails?.city);
    const comment = useSelector((state: RootState) => state.bookingCreation.bookingDetails?.comment);
    const createdBy = useSelector((state: RootState) => state.bookingCreation.bookingDetails?.createdBy);
    const invoiceNumber = useSelector((state: RootState) => state.bookingCreation.bookingDetails?.invoiceNumber);
    const startDate = useSelector((state: RootState) => state.bookingCreation.bookingDetails?.checkIn);
    const endDate = useSelector((state: RootState) => state.bookingCreation.bookingDetails?.checkOut);
    // #3219: every day and time of this booking is its location's clock.
    const timeZone = zoneOfLocation(bookingDetails?.locationTimeZone);
    const numberOfGuests = useSelector((state: RootState) => state.bookingCreation.bookingDetails?.numberOfGuests);
    const apartments = useSelector((state: RootState) => state.bookingCreation.apartments);
    const someoneElseDataValidation: SomeoneElseDataValidation = useSelector((state: RootState) => state.bookingCreation.someoneElseDataValidation);
    const additionalServices = useSelector((state: RootState) => state.bookingCreation.bookingDetails.additionalService ?? []);

    const hasEditBookingsPermission = useSelector(
        makeHasPermissionSelector(Permission.editBookings()),
    );
    // Deleting a booking is a master-only action — hidden from tenant owners
    // even though they hold the deleteBookings permission.
    const isMaster = useIsMaster();

    const tenantConfig = useSelector(
        (state: RootState) => state.authentication.config,
    );
    // Invoice company/bank config: bundled for legacy tenants, derived from
    // account settings for self-service tenants (owner via getAccount, master
    // via the by-slug invoice-info endpoint). See useInvoiceConfig.
    const invoiceConfig = useInvoiceConfig();
    const isInvoiceIssuingTenant = useIsInvoiceIssuingTenant();
    const isCovered = isCoveredByOrganization({ coveredByOrg: bookingDetails?.coveredByOrg });
    // PO, 2026-10-23: list price 0 € — no invoice is needed, neither the
    // server-issued one nor the client-side PDF.
    const isFreeByPrice = isFreeByPriceBooking(bookingDetails);

    const generateInvoice = async (options: InvoiceOptions) => {
        // No breakdown, no invoice (#1203) — handleCreateInvoice already blocks
        // this, and there is deliberately no fallback that re-derives the price.
        if (!invoiceConfig || !tenantConfig || !breakdown) return;
        await generateInvoicePdf({
            booking: bookingDetails,
            breakdown,
            invoiceConfig: invoiceConfig,
            // Uploaded branding logo first (self-service / migrated tenants);
            // bundled tenant logo for legacy tenants. Prevents the kolula
            // fallback logo from landing on a tenant's invoice (#1419 regression).
            tenantLogo: invoiceConfig.logoUrl ?? tenantConfig.logo,
            // `i18n.language` is the raw detected code and is 'de-DE' on a
            // German browser, so the cast below it lies. `getCurrentLang()`
            // normalizes to the 2-letter code the invoice needs — the same
            // value `breakdownLines` looks service names up by, so the
            // document and the screen name a service identically.
            language: getCurrentLang() as 'de' | 'en',
            t,
            dueDate: options.dueDate,
            note: options.note,
        });
    };

    const handleConfirmInvoice = async (options: InvoiceOptions) => {
        setIsInvoiceDialogOpen(false);
        await generateInvoice(options);
    };

    const handleCreateInvoice = () => {
        if (!invoiceConfig || !tenantConfig) return;
        const errors = validateInvoiceData(
            bookingDetails, invoiceConfig, t,
            tenantConfig.country ?? 'DE', breakdown,
        );
        if (errors.length > 0) {
            setInvoiceErrors(errors);
            return;
        }
        setIsInvoiceDialogOpen(true);
    };

    const styles = {
        paper: (theme: any) => ({
            padding: '20px',
        }),
    };

    const handleClickCancelBooking = async () => {
        if (await feeStep.openIfFeeApplies()) return;
        setDialogAction('cancel');
        // Content BEFORE opening, never after. The dialog renders from the
        // store, so opening first would show whatever the last dialog left
        // there: blank on the first cancel of a page visit, and the DELETE
        // text and buttons on a page where delete was opened once — a dialog
        // reading "Buchung löschen" whose button cancels.
        const base = {
            content: t('booking_details.confirmDialog.content'),
            headerText: t('booking_details.confirmDialog.headerText'),
            leftButtonText: t('booking_details.confirmDialog.leftButtonText'),
            middleButtonText: t('booking_details.confirmDialog.middleButtonText'),
            rightButtonText: t('booking_details.confirmDialog.rightButtonText'),
        };
        dispatch(setConfirmationDialogContent(base));
        dispatch(setIsConfirmationDialog(true));

        // The mail history only enriches the question, so it arrives late
        // rather than holding the dialog shut on a slow read — a button that
        // does nothing for two seconds reads as broken.
        const request = ++dialogRequestRef.current;
        const mailHint = await fetchMailHint(orderId);
        // Dropped if anything else opened the dialog meanwhile (closed and
        // reopened as delete, say): a late write would put cancel's text over
        // another action's.
        if (!mailHint || request !== dialogRequestRef.current) return;
        dispatch(
            setConfirmationDialogContent({
                ...base,
                content: `${base.content} ${mailHint}`,
            }),
        );
    };

    const handleClickDeleteBooking = () => {
        setDialogAction('delete');
        // Invalidates any cancel-dialog mail hint still in flight, so it
        // cannot land on top of this dialog's text.
        dialogRequestRef.current += 1;
        dispatch(setIsConfirmationDialog(true));
        dispatch(
            setConfirmationDialogContent({
                content: t('booking_details.confirmDeleteDialog.content'),
                headerText: t('booking_details.confirmDeleteDialog.headerText'),
                leftButtonText: t('booking_details.confirmDeleteDialog.leftButtonText'),
                rightButtonText: t('booking_details.confirmDeleteDialog.rightButtonText'),
            }),
        );
    };

    async function handleCancelBooking(saveMode: CancelSaveMode, fee?: CancellationFeeChoice) {
        try {
            const response = await cancelBooking(saveMode, undefined, fee);
            feeStep.close();
            if (response) {
                const outcome = cancelOutcomeMessage(saveMode, response.mailSent);
                const invoice = invoiceCorrectionMessage(t, response.invoiceCorrection);
                setSnackbarData({
                    open: true,
                    message: invoice ? `${t(outcome.messageKey)} ${invoice.text}` : t(outcome.messageKey),
                    severity: invoice?.failed ? 'warning' : outcome.severity,
                });
                setTimeout(() => {
                    navigate(paths.rentalBooking);
                }, 1500);
            }
        } catch (error) {
            feeStep.close();
            setSnackbarData({
                open: true,
                message: t(cancelErrorMessageKey(error)),
                severity: 'error',
            });
        }
    }

    async function handleDeleteBooking() {
        try {
            const response = await deleteBooking();
            if (response) {
                setSnackbarData({
                    open: true,
                    message: t('booking_details.delete_booking.success_message'),
                    severity: 'success',
                });
                setTimeout(() => {
                    navigate(paths.rentalBooking);
                }, 1500);
            }
        } catch (error) {
            setSnackbarData({
                open: true,
                message: deleteBookingErrorMessage(error, t),
                severity: 'error',
            });
        }
    }

    const handleCloseSnackbar = () => {
        setSnackbarData((prev) => ({ ...prev, open: false }));
    };

    const currentLanguage = i18n.language;

    useEffect(() => {
        if (apartments && apartments.length > 0) {
            setApartmentNames(sortByLocaleName(apartments.map((apart) => {
                if (apart?.langNames?.[currentLanguage]?.title) {
                    return apart?.langNames?.[currentLanguage]?.title;
                } else {
                    return apart?.name;
                }
            }), (name) => name));
        }
    }, [apartments]);

    // Compulsory services arrive separately (payment.compulsory), not in the asset's
    // optional menu. Merge them so the mandatory section and price box show them.
    const { allWithCompulsory: allAdditionalServicesWithCompulsory } =
        buildOrderAddonData(bookingDetails?.payment?.compulsory, bookingDetails?.allAdditionalServices ?? [], additionalServices);

    // The due-date field is only relevant when an amount is still owed.
    const invoiceBalanceDue = bookingDetails
        ? (bookingDetails.payment.total
            - (bookingDetails.payment.couponApplied ?? 0))
            - (bookingDetails.payment.amountPaid ?? 0)
        : 0;
    const showInvoiceDueDate = invoiceBalanceDue > 0;

    const [newSetupTime, setNewSetupTime] = useState<string>('');


    const handleDateChange = async (setupTime: string) => {
        Logger('handle date').log(newSetupTime);
        if (!orderId) return;

        try {
            // #2982: `setupTime` is what the operator reads off the input, i.e.
            // local wall-clock without a zone. Interpreting it as anything else
            // is what moved the stored instant on every save.
            const instant = localInputValueToInstant(setupTime, timeZone);
            if (!instant) return;
            await updateSetupTime({
                bookingId: orderId,
                setupTime: instant,
            }).unwrap();
            refetch();
            dispatch(invalidateSavedBookingPrice());
            setIsChangingDate(false);
        } catch (error) {
            console.error(error);
        }
    };

    Logger('handle date').log(newSetupTime);

    useEffect(() => {
        if (bookingDetails) {
            if (Object.keys(bookingDetails.guestDetails).length > 0) {
                setBookingFor('someone_else');
            } else {
                setBookingFor('customer');
            }
        }
    }, [bookingDetails]);

    useEffect(() => {
        if (data && data.setupTime) {
            Logger('handle date').log(data.setupTime);
            setNewSetupTime(instantToLocalInputValue(data.setupTime, zoneOfLocation(data.locationTimeZone)));
        }
    }, [data]);

    useEffect(() => {
        if (orderId) {
            dispatch(invalidateSavedBookingPrice());
        }
    }, [orderId]);

    return (
        <Container maxWidth={false} className={'px-0 sm:px-4'}>
            {!showBookingDetails ? (
                <Box sx={{ py: 3 }}>
                    <div className={'flex w-full h-[80vh] items-center justify-center'}>
                        <CircularProgress />
                    </div>
                </Box>
            ) : (
                <Box sx={{ py: 3 }}>
                    <PageHeader>
                        <PageHeadline>
                            {t('booking_details.title')}
                        </PageHeadline>
                    </PageHeader>
                    <div className={'mb-6'}>
                        <CustomSeparator items={breadcrumbItems} />
                    </div>
                    <Paper sx={styles.paper}>
                        {/* Header summary */}
                        <div
                            className={`
                        flex justify-between mb-4 bg-[#F5F5F5]
                        p-3
                        `}
                        >
                            <div className={'flex items-center w-[100%]'}>
                                <div className={'flex flex-col w-[100%]'}>
                                    <div className={'flex flex-col xl:flex-row justify-between'}>
                                        <div className={'flex items-center mb-2'}>
                                            <div className={'flex flex-col xl:flex-row'}>
                                                <h1 className={'text-2xl font-medium mr-4'}>{bookingDetails?.bookingNumber}</h1>
                                                <div className={'flex flex-row my-2 xl:my-0'}>
                                                    <StatusBadge
                                                        type={bookingDetails?.status ?? 'booked'}
                                                        isEditing={false}
                                                        options={[
                                                            {
                                                                label: t('booking_details.status.active'),
                                                                value: 'active',
                                                            },
                                                            {
                                                                label: t('booking_details.status.booked'),
                                                                value: 'booked',
                                                            },
                                                            {
                                                                label: t('booking_details.status.canceled'),
                                                                value: 'canceled',
                                                            },
                                                            {
                                                                label: t('booking_details.status.finished'),
                                                                value: 'finished',
                                                            },
                                                        ]}
                                                    />
                                                    <BookingApprovalLabel approval={bookingDetails?.approval} />
                                                    <div className={'mx-2'}></div>
                                                    <StatusBadge
                                                        // #1926: a covered booking is 'unpaid' on purpose — its
                                                        // organization carries it — so it reads "Abgedeckt".
                                                        type={detailPaymentBadgeStatus(bookingDetails)}
                                                        showDropdown={false}
                                                        isEditing={false}
                                                        options={[
                                                            {
                                                                label: t('booking_details.status.paid'),
                                                                // selectable: true,
                                                                value: 'paid',
                                                            },
                                                            {
                                                                label: t('booking_details.status.unpaid'),
                                                                // selectable: true,
                                                                value: 'unpaid',
                                                            },
                                                            {
                                                                label: t('booking_details.status.partially_paid'),
                                                                // selectable: false,
                                                                value: 'partially_paid',
                                                            },
                                                            {
                                                                label: t('booking_details.status.overpaid'),
                                                                // selectable: false,
                                                                value: 'overpaid',
                                                            },
                                                        ]}
                                                    />
                                                    <div className={'mx-2'}></div>
                                                    <StatusBadge
                                                        type={mailBadge.type}
                                                        showDropdown={false}
                                                        isEditing={false}
                                                        options={[
                                                            {
                                                                label: t('booking_details.mail_sent.mail_sent'),
                                                                value: 'sent',
                                                            },
                                                            {
                                                                label: t('booking_details.mail_sent.mail_not_sent'),
                                                                value: 'not_sent',
                                                            },
                                                            {
                                                                label: t('booking_details.mail_sent.change_not_sent'),
                                                                value: 'change_not_sent',
                                                            },
                                                            {
                                                                label: t('booking_details.mail_sent.mail_blocked'),
                                                                value: 'mail_blocked',
                                                            },
                                                        ]}
                                                    />
                                                    <CustomerMailBlockedReason reason={mailBadge.blockedReason} />
                                                    {/* #1993: which mail, when, and — when the badge
                                                        says a change went untold — when that was.
                                                        Absent while the events load or if the read
                                                        failed, rather than asserting a history we
                                                        could not read. */}
                                                    {mailBadge.tooltip && (
                                                        <span className={'ml-1'}>
                                                            <InfoTooltip text={mailBadge.tooltip} />
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className={'flex flex-wrap gap-2 mb-2'}>
                                            {isMaster && (
                                                <div className={'flex flex-wrap gap-2 xl:justify-end xl:mb-0'}>
                                                    <CustomButton
                                                        title={t('booking_details.buttons.delete_booking')}
                                                        onClick={handleClickDeleteBooking}
                                                        isFilled={false}
                                                        variant={'delete'}
                                                    />
                                                </div>
                                            )}
                                            {hasEditBookingsPermission && (
                                                bookingDetails.status === 'canceled' ? (
                                                    <div className={'flex flex-wrap gap-2 xl:justify-end'}>
                                                        <CustomButton
                                                            title={t('booking_details.buttons.edit_button')}
                                                            onClick={() => {
                                                                navigate(makeRoomBookingEditsUrl(orderId));
                                                            }}
                                                            isFilled
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className={'flex flex-wrap gap-2 xl:justify-end xl:mb-0'}>
                                                        <CustomButton
                                                            title={t('booking_details.buttons.edit_button')}
                                                            onClick={() => {
                                                                navigate(makeRoomBookingEditsUrl(orderId));
                                                            }}
                                                            isFilled
                                                        />
                                                        {isAwaitingApproval && (
                                                            <CustomButton
                                                                title={t('booking_details.buttons.decline_booking')}
                                                                onClick={decline.openDeclineDialog}
                                                                isFilled={false}
                                                            />
                                                        )}
                                                        <CustomButton
                                                            title={t('booking_details.buttons.cancel_booking')}
                                                            onClick={handleClickCancelBooking}
                                                            isFilled={false}
                                                        />
                                                    </div>

                                                )
                                            )}
                                            {/* #1637: retired for Mode A, with or without a number circle (no
                                        scheme, no invoicing: ADR-0012, PO 2026-09-29). kolula now issues a
                                                numbered, archived invoice server-side, and a
                                                second unnumbered PDF beside it would compete
                                                with the statutory document. Mode C tenants do not
                                                get it either — their own ERP issues the invoice
                                                (`useInvoiceConfig` returns undefined for them); a tenant
                                                with no issuer set keeps it — except on a covered
                                                booking: the person who booked owes nothing, so
                                                no invoice is addressed to them (ADR-0008 Layer 3). */}
                                            {invoiceConfig && !isInvoiceIssuingTenant && !isCovered && !isFreeByPrice && (
                                                <CustomButton
                                                    title={t('invoice.create_invoice')}
                                                    onClick={handleCreateInvoice}
                                                    isFilled={false}
                                                />
                                            )}
                                            {/* PO, 2026-10-23: a booking free by price needs no invoice — the note
                                                takes the button's place (IssueInvoiceSection does the same for Mode A). */}
                                            {invoiceConfig && !isInvoiceIssuingTenant && !isCovered && isFreeByPrice && (
                                                <p className={'text-sm text-foreground/60'}>{t('account.invoice_free_by_price')}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className={'flex w-full gap-4 flex-col items-start sm:flex-row sm:justify-between sm:items-center'}>
                                        <div className={'flex-1'}>
                                            <p className={'text-sm text-gray-500'}>
                                                {t('booking_details.created_on')}:{' '}
                                                <span className={'font-bold'}>
                                                    {formatDateForDisplay(bookingDetails?.created_at!, timeZone)}
                                                </span>
                                            </p>
                                            <p className={'text-sm text-gray-500'}>
                                                {t('booking_details.last_update')}:{' '}
                                                <span className={'font-bold'}>
                                                    {formatDateForDisplay(bookingDetails?.updated_at!, timeZone)}
                                                </span>
                                            </p>
                                            {bookingDetails?.rentalStartDateTime && (
                                                <p className={'text-sm text-gray-500 mb-1'}>
                                                    {t('booking_details.started_at')}:{' '}
                                                    <span className={'font-bold'}>{formatDateForDisplay(bookingDetails?.rentalStartDateTime, timeZone)}</span>
                                                </p>
                                            )}
                                            {bookingDetails?.rentalEndDateTime && (
                                                <p className={'text-sm text-gray-500'}>
                                                    {t('booking_details.finished_at')}:{' '}
                                                    <span className={'font-bold'}>{formatDateForDisplay(bookingDetails?.rentalEndDateTime, timeZone)}</span>
                                                </p>
                                            )}
                                            <ContractConcludedInfo
                                                contractConcludedAt={bookingDetails?.contractConcludedAt}
                                                paymentStatus={bookingDetails?.payment_status}
                                                coveringOrganizationName={coveringOrganizationNameOf(bookingDetails)}
                                                timeZone={timeZone}
                                            />
                                            <BookingApprovalInfo approval={bookingDetails?.approval} timeZone={timeZone} />
                                            <CancellationFeeInfo fee={bookingDetails?.cancellationFee} />
                                            {usageLog?.activatedAt && (
                                                <p className={'text-sm text-gray-500 mt-1'}>
                                                    {t('booking_details.checked_in_at')}:{' '}
                                                    <span className={'font-bold'}>{formatDateForDisplay(usageLog.activatedAt, timeZone)}</span>
                                                </p>
                                            )}
                                            {usageLog?.finishedAt && (
                                                <p className={'text-sm text-gray-500'}>
                                                    {t('booking_details.checked_out_at')}:{' '}
                                                    <span className={'font-bold'}>{formatDateForDisplay(usageLog.finishedAt, timeZone)}</span>
                                                </p>
                                            )}
                                            {bookingDetails?.setupTime && (
                                                <p className={'text-sm text-gray-500'}>
                                                    <span className={'space-x-2 flex items-center'}>
                                                        {isChangingDate ? (
                                                            <span className={'flex space-x-2 items-center'}>
                                                                <span>{t('booking_details.buffer_from')}:</span>
                                                                <input
                                                                    type={'datetime-local'}
                                                                    value={newSetupTime}
                                                                    onChange={(e) => setNewSetupTime(e.target.value)}
                                                                    className={'border rounded px-2 py-1'}
                                                                />
                                                                <CustomButton
                                                                    onClick={() => handleDateChange(newSetupTime)}
                                                                    disabled={!newSetupTime}
                                                                    title={t('common.save')}
                                                                    isFilled
                                                                />
                                                                <CustomButton
                                                                    onClick={() => setIsChangingDate(false)}
                                                                    title={t('common.cancel')}
                                                                    isFilled={false}
                                                                />
                                                            </span>
                                                        ) : (
                                                            <span className={'flex space-x-2 items-center'}>
                                                                <span className={'inline-flex items-center gap-1'}>
                                                                    {t('booking_details.buffer_from')}:
                                                                    <InfoTooltip text={t('booking_details.buffer_from_tooltip')} />
                                                                </span>
                                                                <span
                                                                    className={'font-bold'}
                                                                >
                                                                    {formatDateTime(
                                                                        bookingDetails.setupTime,
                                                                        i18n.language as 'en' | 'de',
                                                                        { includeTime: true, timeZone },
                                                                    )}
                                                                </span>
                                                                <span
                                                                    className={'underline cursor-pointer text-primary-500'}
                                                                    onClick={() => {
                                                                        setIsChangingDate(true);
                                                                        setNewSetupTime(newSetupTime ?? '');
                                                                    }}
                                                                >
                                                                    {t('common.change')}
                                                                </span>
                                                                <span
                                                                    className={'underline cursor-pointer text-red-500'}
                                                                    onClick={() => handleDateChange(new Date().toISOString())}
                                                                >
                                                                    {t('common.end')}
                                                                </span>
                                                            </span>
                                                        )}
                                                    </span>
                                                </p>
                                            )}
                                            {bookingDetails?.lockerLastOpenTime && (
                                                <p className={'text-sm text-gray-500'}>
                                                    {t('booking_details.lastLockerOpenDate')}:{' '}
                                                    <span className={'font-bold'}>{formatDateForDisplay(bookingDetails?.lockerLastOpenTime, timeZone)}</span>
                                                </p>
                                            )}
                                        </div>
                                        <ManualInvoiceNumber
                                            invoiceNumber={invoiceNumber}
                                            isInvoiceIssuingTenant={isInvoiceIssuingTenant}
                                        />
                                    </div>
                                    {usageLog?.returnPhoto?.url && (
                                        <div className={'w-full flex justify-center mt-4'}>
                                            <ReturnPhotoThumbnail
                                                photos={[{
                                                    key: 'checkout',
                                                    url: usageLog.returnPhoto.url,
                                                    uploadedAt: usageLog.returnPhoto.uploadedAt,
                                                }]}
                                                status={usageLog.returnPhoto.status}
                                                timeZone={timeZone}
                                                isLoading={false}
                                                isError={false}
                                                titleKey={'booking_details.check_out_photo'}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <Typography variant={'h3'} className={'mb-10 mt-5'}>
                            {t('create_booking.reservation_details.title')}
                        </Typography>
                        <Grid
                            container
                            spacing={2}
                            sx={{
                                width: '100%',
                                justifyContent: 'flex-start',
                                alignItems: 'flex-start',
                                display: 'flex',
                                marginLeft: '0px',
                            }}
                        >
                            <Grid
                                container
                                spacing={2}
                                className={'w-[100%] xl:w-[30%] mt-1 xl:mt-0 mb-8 xl:mb-0 flex justify-end items-end order-1 xl:order-2'}
                            >
                                <ReservationSummary
                                    isCreateBooking={false}
                                    isEditBooking={false}
                                    breakdown={breakdown}
                                    previewStatus={previewStatus}
                                    couponApplied={bookingDetails?.payment?.couponApplied ?? 0}
                                    total={bookingDetails?.payment?.total ?? 0}
                                    amountPaid={bookingDetails?.payment?.amountPaid ?? 0}
                                    coveredBy={coveredByOf(bookingDetails)}
                                />
                            </Grid>
                            <Grid container spacing={2} className={'w-[100%] xl:w-[70%] flex justify-start items-start order-2 xl:order-1'}>
                                <Grid item xs={12} sm={6}>
                                    <BookingDatePicker
                                        disabled={true}
                                        value={stationPickerDay(startDate, timeZone)}
                                        shouldDisableDate={() => false}
                                        label={t('create_booking.reservation_details.check_in')}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <BookingDatePicker
                                        disabled={true}
                                        value={stationPickerDay(endDate, timeZone)}
                                        shouldDisableDate={(date) => (startDate
                                            ? (date ? date.isSameOrBefore(stationPickerDay(startDate, timeZone)) : false)
                                            : false)}
                                        label={t('create_booking.reservation_details.check_out')}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <MultiSelectComponent
                                        options={['']}
                                        value={city ?? ''}
                                        onChange={(value) => dispatch(setCity({ city: value }))}
                                        disabled={true}
                                        label={t('create_booking.reservation_details.city')}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextFieldComponent
                                        type={'number'}
                                        onChange={(value) =>
                                            dispatch(
                                                setNumberOfGuests({
                                                    numberOfGuests: value,
                                                }),
                                            )
                                        }
                                        disabled={true}
                                        value={numberOfGuests}
                                        fullWidth={true}
                                        label={t('create_booking.reservation_details.number_of_guests')}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12}>
                                    <MultiSelectComponent
                                        options={apartmentNames ?? []}
                                        value={bookingDetails.apartment?.name ?? null}
                                        onChange={(apart) => {
                                            if (apartments) {
                                                const selected = apartments.find((apartment) => apartment.name === apart);
                                                if (selected) {
                                                    dispatch(setSelectedApartment(selected));
                                                }
                                            }
                                        }}
                                        disabled={true}
                                        label={t('create_booking.reservation_details.apartment')}
                                    />
                                    {bookingDetails.apartment?.name && bookingDetails.address && (
                                        <Typography variant={'body1'} fontWeight={'bold'} sx={{ mt: 2, color: 'gray' }}>
                                            {bookingDetails.apartment?.id ? (
                                                <Link
                                                    to={makeAssetDetailsUrl(bookingDetails.apartment.id)}
                                                    className={'hover:text-primary hover:underline transition-colors'}
                                                >
                                                    {`${extractAddress(bookingDetails.apartment?.name)}, ${bookingDetails.address.street}, ${
                                                        bookingDetails.address.postalCode
                                                    } ${bookingDetails.address.city}`}
                                                </Link>
                                            ) : (
                                                `${extractAddress(bookingDetails.apartment?.name)}, ${bookingDetails.address.street}, ${
                                                    bookingDetails.address.postalCode
                                                } ${bookingDetails.address.city}`
                                            )}
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />
                        <Typography variant={'h3'} className={'mb-2'}>
                            {t('create_booking.additional_services.title')}
                        </Typography>
                        {bookingDetails ? (
                            bookingDetails.additionalService ? (
                                <BookingDetailsAdditionalServices
                                    additionalServices={additionalServices}
                                    allAdditionalServices={allAdditionalServicesWithCompulsory}
                                />
                            ) : (
                                <div className={'flex items-center space-x-2 text-gray-500'}>
                                    <CircularProgress className={'text-gray-500'} size={20} />
                                    <Typography className={'text-gray-500'}>{t('common.loading')}</Typography>
                                </div>
                            )
                        ) : (
                            <Typography>{t('create_booking.additional_services.note')}</Typography>
                        )}
                        <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />
                        <Typography variant={'h3'} className={'mb-5'}>
                            {t('create_booking.customer_details.title')}
                        </Typography>
                        <Grid container={true} spacing={2} className={'w-[100%] xl:w-[70%]'} sx={{ justifyContent: 'flex-start' }}>
                            {/* First row with two Selects */}
                            <Grid item xs={12} sm={6}>
                                <TextFieldComponent
                                    type={'string'}
                                    onChange={onFirstNameChange}
                                    disabled={true}
                                    value={personalDetails?.firstName ?? ''}
                                    fullWidth={true}
                                    label={t('create_booking.customer_details.first_name')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextFieldComponent
                                    type={'string'}
                                    onChange={onLastNameChange}
                                    disabled={true}
                                    value={personalDetails?.lastName ?? ''}
                                    fullWidth={true}
                                    label={t('create_booking.customer_details.last_name')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextFieldComponent
                                    type={'string'}
                                    onChange={onEmailChange}
                                    disabled={true}
                                    value={personalDetails?.email ?? ''}
                                    fullWidth={true}
                                    label={t('create_booking.customer_details.email')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <PhoneNumberComponent
                                    enabled={false}
                                    initialValue={personalDetails?.phone ?? ''}
                                    onChange={onPhoneChange}
                                    required={false}
                                    label={t('create_booking.customer_details.phone')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <TextFieldComponent
                                    type={'string'}
                                    onChange={() => {}}
                                    disabled={true}
                                    value={personalDetails?.company ?? ''}
                                    fullWidth={true}
                                    label={t('create_booking.customer_details.company')}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                {/* #1810: who the booking is made for, and the cost centre. */}
                                <BookingAttributionReadOnly booking={data} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <TextFieldComponent
                                    type={'string'}
                                    onChange={onAddressChange}
                                    disabled={true}
                                    value={personalDetails?.address ?? ''}
                                    fullWidth={true}
                                    label={t('create_booking.customer_details.address')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <MultiSelectComponent
                                    options={countryOptions}
                                    value={toCountryLabel(personalDetails?.country ?? '')}
                                    onChange={(label) => onCountryChange(toCountryValue(label))}
                                    disabled={true}
                                    label={t('create_booking.customer_details.country')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextFieldComponent
                                    type={'string'}
                                    onChange={onCityChange}
                                    disabled={true}
                                    value={personalDetails?.city ?? ''}
                                    fullWidth={true}
                                    label={t('create_booking.customer_details.city')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextFieldComponent
                                    type={'string'}
                                    onChange={onZipCodeChange}
                                    disabled={true}
                                    value={personalDetails?.zipCode ?? ''}
                                    fullWidth={true}
                                    label={t('create_booking.customer_details.zip_code')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <TextFieldComponent
                                    type={'string'}
                                    onChange={() => {}}
                                    disabled={true}
                                    value={comment ?? ''}
                                    fullWidth={true}
                                    label={t(commentLabelKey(createdBy))}
                                />
                            </Grid>
                        </Grid>

                        {bookingFor === 'someone_else' && <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />}
                        {bookingFor === 'someone_else' && (
                            <Typography variant={'h3'} className={'mb-3'}>
                                {t('booking_details.guest')}
                            </Typography>
                        )}

                        {bookingFor === 'someone_else' && (
                            <Grid
                                container={true}
                                spacing={2}
                                className={'w-[100%] xl:w-[70%]'}
                                sx={{
                                    justifyContent: 'flex-start',
                                }}
                            >
                                <Grid item xs={12} sm={6}>
                                    <TextFieldComponent
                                        type={'string'}
                                        onChange={onSomeoneElseFirstNameChange}
                                        disabled={true}
                                        value={someoneElseDetails?.firstName}
                                        valid={someoneElseDataValidation.firstName}
                                        fullWidth={true}
                                        label={t('create_booking.booking_for.first_name')}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextFieldComponent
                                        type={'string'}
                                        onChange={onSomeoneElseLastNameChange}
                                        disabled={true}
                                        value={someoneElseDetails?.lastName}
                                        valid={someoneElseDataValidation.lastName}
                                        fullWidth={true}
                                        label={t('create_booking.booking_for.last_name')}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextFieldComponent
                                        type={'string'}
                                        onChange={onSomeoneElseEmailChange}
                                        disabled={true}
                                        value={someoneElseDetails?.email}
                                        fullWidth={true}
                                        label={t('create_booking.booking_for.email')}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <PhoneNumberComponent
                                        enabled={false}
                                        initialValue={someoneElseDetails?.phone}
                                        onChange={onSomeoneElsePhoneChange}
                                        required={false}
                                        label={t('create_booking.booking_for.phone')}
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </Paper>
                </Box>
            )}
            <CancellationFeeDialog
                open={feeStep.open}
                quote={feeStep.quote}
                loading={loadingCancelBooking}
                onClose={feeStep.close}
                onConfirm={handleCancelBooking}
            />
            <ConfirmationDialog
                isLoading={loadingCancelBooking}
                handleClickConfirm={
                    dialogAction === 'cancel'
                        ? () => handleCancelBooking('save_and_send_email')
                        : handleDeleteBooking
                }
                handleClickMiddle={
                    dialogAction === 'cancel' ? () => handleCancelBooking('save_only') : undefined
                }
            />
            <DeclineBookingDialog
                open={decline.isDeclineDialogOpen}
                loading={loadingCancelBooking}
                onClose={decline.closeDeclineDialog}
                onConfirm={decline.confirmDecline}
            />
            {/* Server-side issuance (#1637 WP2). Additive beside the
                client-side PDF button above: the jsPDF path is retired once an
                invoice has been issued and re-downloaded for real. */}
            <div className={'mt-4'}>
                <IssueInvoiceSection
                    orderId={orderId}
                    editBookingUrl={makeRoomBookingEditsUrl(orderId)}
                    isCovered={isCovered}
                    isFreeByPrice={isFreeByPrice}
                    coveringOrganizationName={coveringOrganizationNameOf(bookingDetails)}
                    defaultNote={invoiceConfig?.defaultInvoiceNote}
                    isPaidInFull={!showInvoiceDueDate}
                    hasPriceAdjustment={hasPriceAdjustment(breakdown)}
                />
            </div>

            <SnackbarComponent
                open={snackbarData.open}
                message={snackbarData.message}
                severity={snackbarData.severity}
                onClose={handleCloseSnackbar}
            />
            <InvoiceOptionsDialog
                open={isInvoiceDialogOpen}
                paymentTermDays={invoiceConfig?.paymentTermDays}
                showDueDate={showInvoiceDueDate}
                defaultNote={invoiceConfig?.defaultInvoiceNote}
                onConfirm={handleConfirmInvoice}
                onClose={() => setIsInvoiceDialogOpen(false)}
            />
            {invoiceErrors.length > 0 && (
                <div className={'fixed inset-0 z-[1000] flex items-center justify-center p-4'}>
                    <div
                        className={'fixed inset-0 bg-black bg-opacity-50'}
                        onClick={() => setInvoiceErrors([])}
                    />
                    <div className={'relative z-10 bg-white rounded-lg shadow-xl w-full max-w-md mx-auto'}>
                        <div className={'p-6'}>
                            <h3 className={'text-lg font-bold text-gray-900 mb-2'}>
                                {t('invoice.validation_error_title')}
                            </h3>
                            <p className={'text-sm text-gray-600 mb-4'}>
                                {t('invoice.validation_error_description')}
                            </p>
                            <ul className={'space-y-2'}>
                                {invoiceErrors.map((err) => (
                                    <li
                                        key={err.field}
                                        className={'flex items-start text-sm text-red-600'}
                                    >
                                        <span className={'mr-2'}>&#x2717;</span>
                                        {err.message}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className={'px-6 py-4 flex justify-end'}>
                            <CustomButton
                                title={t('invoice.close')}
                                onClick={() => setInvoiceErrors([])}
                                isFilled
                            />
                        </div>
                    </div>
                </div>
            )}
            {/* #2002: the money trail, collapsed by default. Payment status
                stays where it is; this is the detail one click away. */}
            {orderId && (
                <div className={'mt-4'}>
                    <PaymentHistorySection orderId={orderId} />
                </div>
            )}
        </Container>
    );
};

export default BookingDetailsForRoomRental;
